import React, { Component } from "react";
import { Col, Table, Row, notification, Input, Switch, Select } from "antd";
import { ContentWrapper, Icon, ProgressBarSteps, Table as CompTable } from "components";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { capitalize, formatPrice, getName, getSuccessLevelBadge, translate, xlsxSettings } from "utils/utils";
import { getColumnFilter } from "utils/comp-utils";
import moment from "moment";
import xlsx from "json-as-xlsx";
import { language } from "store/reducers/mix";

class Incentives extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			data: [],
			// startDate: moment().add({ months: -5 }).startOf("month"),
			// endDate: moment().endOf("month"),
			startDate: moment().startOf("year"),
			endDate: moment(),
			partner: undefined,
			partnerOptions: [],
			partnerSearch: ""
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		await this.getData();
		this.breadcrumb();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData(selectedBonus = "") {
		const { startDate, endDate, partner } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		const body = {
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString(),
			type: "incentives",
			selectedBonus,
			partner
		};

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("search-by-type"),
				data: body
			});

			if (response.ok) {
				const {
					commissions = [],
					partners = [],
					settings,
					eligible,
					partner,
					currentBonus,
					accumulatedTpv,
					movements = []
				} = response.data.results;

				let totals = commissions?.totals[0] || {};

				this.setState({
					data: commissions.items,
					movements,
					partners,
					settings,
					eligible,
					partnerDetail: partner,
					groupTrip: settings?.bonuses?.groupTrip || {},
					currentBonus,
					accumulatedTpv,
					cards: {
						cycles: totals?.totalCycles || 0,
						available: totals?.totalAvailable || 0,
						spent: totals?.usedBalance || 0,
						expired: totals?.totalExpiredValue || 0,
						total: totals?.totalValue || 0
					}
				});
			} else {
				notification.error({
					message: strings.commissions.incentives,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err: any) {
			const description = err?.response?.data?.message || err?.message || JSON.stringify(err, null, 2) || "Erro inesperado";

			notification.error({
				message: strings.serverErrors.title,
				description: description,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	handleSearch(partner: any) {
		this.setState({ partnerSearch: partner });

		if (partner.length < 3) return;

		if (this.searchTimeout) clearTimeout(this.searchTimeout);

		this.searchTimeout = setTimeout(async () => {
			this.getUsers();
		}, 500);
	}

	async getUsers() {
		const { partnerSearch } = this.state;

		try {
			const body = { search: partnerSearch, page: 0, perPage: 10 };

			const response = await API.post({
				url: Endpoints.uriPartners("search"),
				data: body
			});

			if (response.ok) {
				const { users = [] } = response.data.results || {};
				this.setState({ partnerOptions: users });
			} else {
				notification.error({
					message: strings.commissions.incentives,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate, partnerOptions } = this.state;

				return {
					locations: [
						{
							text: strings.commissions.incentives,
							icon: "organization"
						}
					],
					actions: [
						{
							type: "select",
							text: strings.fields.partner,
							placeholder: strings.generic.searchByEmailOrName,
							options: partnerOptions.map((partner: any) => ({
								value: partner._id,
								text: `${partner.name} (${partner.email})`
							})),
							onChange: (partner: any) => {
								this.setState({ partner }, () => this.getData());
							},
							value: this.state.partner,
							showSearch: true,
							onSearch: (value: string) => this.handleSearch(value),
							separator: "right",
							style: { width: 300, height: 40 },
							allowClear: true
						},
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	renderCards() {
		const { cards } = this.state;
		const { cycles = 0, available = 0, spent = 0, expired = 0, total = 0 } = cards || {};

		const CARDS = [
			{
				icon: "wallet",
				value: total,
				description: strings.commissions.withThisBonus,
				type: "currency"
			},
			{
				icon: "refresh",
				value: cycles,
				description: strings.commissions.totalCycles
			},
			{
				icon: "wallet",
				value: available,
				description: strings.wallet.available,
				type: "currency"
			},
			{
				icon: "wallet",
				value: spent,
				description: strings.wallet.spent,
				type: "currency"
			},
			{
				icon: "wallet",
				value: expired,
				description: strings.wallet.expired,
				type: "currency"
			}
		];

		return CARDS.map((card, index) => (
			<Col xs={24} md={6} key={index}>
				<div className="IconCardOuterContainer">
					<div className="IconCard">
						<div className="IconCardHeader">
							<div className="IconCardHeaderMask" />
							<div className="IconCardType">
								<Icon name={card.icon} />
							</div>
						</div>
						<div className="IconCardBody">
							<span className="IconCardValue">{card.type === "currency" ? formatPrice(card.value) : card.value}</span>
							<h3 className="IconCardDescription">{card.description}</h3>
						</div>
					</div>
				</div>
			</Col>
		));
	}

	renderMovements() {
		const { partner } = this.state;

		if (partner) return this.renderPartnerMovements();
		return this.renderAllMovements();
	}

	exportAllData = () => {
		const { data: exportData = [] } = this.state;

		const columns = [
			{ label: "Data", value: (row: any) => DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm") },
			{ label: "Parceiro", value: (row: any) => `${row.partner.name} (#${row.partner.userCode})` },
			{ label: "Nível de Sucesso", value: (row: any) => getSuccessLevelBadge(row.partner.successLevel) },
			{ label: "Descrição", value: "notes" },
			{ label: "Valor", value: (row: any) => (row.value ? formatPrice(row.value) : row.prize) },
			{
				label: "Data de Expiração",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "-")
			}
		];

		const data = [
			{
				sheet: "Incentivos Extra",
				columns,
				content: exportData
			}
		];

		xlsx(data, xlsxSettings("Incentivos Extra"));
	};

	// renderAllMovements() {
	// 	const { data = [] } = this.state;
	// 	const { countries } = this.props;

	// 	return (
	// 		<ContentWrapper>
	// 			<div className="ScreenHeader --multi">
	// 				<div className="ScreenHeaderLeft">
	// 					<Icon name="diagram" />
	// 					<h2>{strings.commissions.incentives}</h2>
	// 				</div>
	// 				<div className="ScreenHeaderRight">
	// 					<button className="EthicAddButton" onClick={this.exportAllData}>
	// 						<Icon name="print" />
	// 					</button>
	// 				</div>
	// 			</div>
	// 			{/* <Table
	// 				style={{ marginTop: 20 }}
	// 				columns={[
	// 					{
	// 						title: strings.fields.date,
	// 						dataIndex: "date",
	// 						key: "date",
	// 						render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
	// 						defaultSortOrder: "descend",
	// 						sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
	// 						width: 150
	// 					},
	// 					{
	// 						title: strings.fields.partner,
	// 						dataIndex: "partner",
	// 						key: "partner",
	// 						render: (partner: any) => {
	// 							if (!partner) return "-";
	// 							const countryCallingCode = partner.phone?.substring(0, 4).replace("+", "");
	// 							let country;

	// 							if (countryCallingCode) {
	// 								country = countries
	// 									.find((elem: any) => elem.callingCodes.includes(countryCallingCode))
	// 									?.alpha2Code?.toLowerCase();
	// 							}

	// 							return (
	// 								<span className="TableRowAligned">
	// 									{country ? (
	// 										<div className="react-tel-input">
	// 											<div className="selected-flag">
	// 												<span className={`flag ${country}`} />
	// 											</div>
	// 										</div>
	// 									) : null}
	// 									{partner.name}
	// 									{Boolean(partner?.userCode) ? ` (#${partner.userCode})` : ""}
	// 								</span>
	// 							);
	// 						},
	// 						sorter: (a, b) => a.partner.name?.localeCompare(b.partner.name)
	// 					},
	// 					{
	// 						title: strings.users.partnerRanking,
	// 						dataIndex: "actualSuccessLevel",
	// 						key: "actualSuccessLevel",
	// 						render: (_: string, record: any) => getSuccessLevelBadge(record?.partner?.successLevel) || "-",
	// 						sorter: (a, b) => a.partner?.successLevel?.localeCompare(b.partner?.successLevel)
	// 					},
	// 					{
	// 						title: strings.fields.description,
	// 						dataIndex: "notes",
	// 						key: "notes",
	// 						render: (notes: string) => notes || "-",
	// 						sorter: (a, b) => a.description?.localeCompare(b.description)
	// 					},
	// 					{
	// 						title: strings.commissions.value,
	// 						dataIndex: "value",
	// 						key: "value",
	// 						render: (value: number) => formatPrice(value),
	// 						sorter: (a, b) => a.value - b.value
	// 					},
	// 					{
	// 						title: strings.generic.expirationDate,
	// 						dataIndex: "expirationDate",
	// 						key: "expirationDate",
	// 						render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
	// 						align: "center",
	// 						sorter: (a, b) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
	// 						width: 150
	// 					}
	// 				]}
	// 				dataSource={data}
	// 				rowKey={(record) => record._id}
	// 			/> */}

	// 			<Table
	// 				style={{ marginTop: 20 }}
	// 				columns={[
	// 					{
	// 						title: strings.fields.date,
	// 						dataIndex: "date",
	// 						key: "date",
	// 						render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
	// 						defaultSortOrder: "descend",
	// 						sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
	// 						width: 150
	// 					},
	// 					{
	// 						title: strings.fields.partner,
	// 						dataIndex: "partner",
	// 						key: "partner",
	// 						render: (partner: any) => {
	// 							if (!partner) return "-";
	// 							const countryCallingCode = partner.phone?.substring(0, 4).replace("+", "");
	// 							let country;

	// 							if (countryCallingCode) {
	// 								country = countries
	// 									.find((elem: any) => elem.callingCodes.includes(countryCallingCode))
	// 									?.alpha2Code?.toLowerCase();
	// 							}

	// 							return (
	// 								<span className="TableRowAligned">
	// 									{country ? (
	// 										<div className="react-tel-input">
	// 											<div className="selected-flag">
	// 												<span className={`flag ${country}`} />
	// 											</div>
	// 										</div>
	// 									) : null}
	// 									{partner.name}
	// 									{Boolean(partner?.userCode) ? ` (#${partner.userCode})` : ""}
	// 								</span>
	// 							);
	// 						},
	// 						sorter: (a, b) => a.partner.name?.localeCompare(b.partner.name)
	// 					},
	// 					{
	// 						title: strings.users.partnerRanking,
	// 						dataIndex: "actualSuccessLevel",
	// 						key: "actualSuccessLevel",
	// 						render: (_: string, record: any) => getSuccessLevelBadge(record?.partner?.successLevel) || "-",
	// 						sorter: (a, b) => a.partner?.successLevel?.localeCompare(b.partner?.successLevel)
	// 					},
	// 					{
	// 						title: strings.fields.description,
	// 						dataIndex: "record",
	// 						key: "notes",
	// 						render: (_: any, record: any) => record?.notes || strings.bonuses.bonusExtraIncentivePrize, // If it does not have notes it is because it comes from the reports collection
	// 						sorter: (a, b) => a.notes?.localeCompare(b.notes)
	// 					},
	// 					{
	// 						title: strings.generic.bonus,
	// 						dataIndex: "bonus",
	// 						key: "bonus",
	// 						render: (_: any, record: any) => {
	// 							if (record.value != null) return formatPrice(record.value);
	// 							return capitalize(record.prize);
	// 						}
	// 					},
	// 					{
	// 						title: strings.commissions.accumulatedTpv,
	// 						dataIndex: "tpv",
	// 						key: "tpv",
	// 						render: (tpv: number) => tpv || 0
	// 					},
	// 					{
	// 						title: strings.generic.expirationDate,
	// 						dataIndex: "expirationDate",
	// 						key: "expirationDate",
	// 						render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
	// 						align: "center",
	// 						sorter: (a, b) => DateTime.fromISO(a.expirationDate).toMillis() - DateTime.fromISO(b.expirationDate).toMillis(),
	// 						width: 150
	// 					}
	// 				]}
	// 				dataSource={data}
	// 				rowKey={(record) => record._id}
	// 			/>
	// 		</ContentWrapper>
	// 	);
	// }

	exportData = () => {
		const { data: exportData = [], partner } = this.state;
		const { countries = [], partnerOptions = [] } = this.props;

		const columns: any[] = [
			{
				label: "Data",
				value: (row: any) => DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm")
			},
			{
				label: "Valor",
				value: (row: any) => formatPrice(row.value)
			},
			{
				label: "Validade",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "-")
			}
		];

		if (!partner) {
			columns.splice(
				1,
				0,
				// ID
				{
					label: "ID Parceiro",
					value: (row: any) => `#${row.partner?.userCode || "-"}`
				},
				// País
				{
					label: "País",
					value: (row: any) => {
						const phone = row.partner?.phone;
						if (!phone) return "-";

						const callingCode = phone.substring(0, 4).replace("+", "");
						const country = countries.find((c: any) => c.callingCodes.includes(callingCode));

						return country?.name || "-";
					}
				},
				// Nome
				{
					label: "Nome",
					value: (row: any) => row.partner?.name || "-"
				},
				// Nível de Sucesso
				{
					label: "Nível de Sucesso (na data)",
					value: (row: any) => getSuccessLevelBadge(row.partner?.successLevel) || "-"
				},
				// Cycles
				{
					label: "Volume Pessoal Acumulado",
					value: (row: any) => row.tpv || 0
				},
				// Incentivo
				{
					label: "Designação do Incentivo",
					value: (row: any) => row.notes || "-"
				}
			);
		}

		const sheets = [
			{
				sheet: "Movimentos",
				columns,
				content: exportData
			}
		];

		let name = "Movimentos";
		if (partner) {
			const partnerData = partnerOptions.find((elem: any) => elem._id === partner);
			if (partnerData) name = partnerData.name;
		}

		xlsx(sheets, xlsxSettings(name));
	};

	renderAllMovements() {
		const { data = [], partner } = this.state;
		const { countries = [] } = this.props;

		const columns: any[] = [
			{
				title: strings.fields.date,
				dataIndex: "date",
				key: "date",
				render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
				defaultSortOrder: "descend",
				sorter: (a: any, b: any) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
				width: 150
			},
			{
				title: strings.commissions.value,
				dataIndex: "value",
				key: "value",
				render: (value: number) => formatPrice(value),
				sorter: (a: any, b: any) => a.value - b.value,
				width: 150
			},
			{
				title: "Validade",
				dataIndex: "expirationDate",
				key: "expirationDate",
				render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
				sorter: (a: any, b: any) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
				width: 150
			}
		];

		if (!partner) {
			columns.splice(
				1,
				0,
				{
					title: "Id",
					dataIndex: "partner",
					key: "userCode",
					render: (partner: any) => {
						return `#${partner?.userCode}` || "-";
					},
					sorter: (a: any, b: any) => a.partner?.userCode - b.partner?.userCode,
					...getColumnFilter(["partner.name", "partner.email", "partner.userCode"]),
					width: 60
				},
				{
					title: strings.fields.country,
					dataIndex: "partner",
					key: "country",
					sorter: (a: any, b: any) => {
						if (!a || !b) return;
						const aCountryCallingCode = a.partner.phone?.substring(0, 4).replace("+", "");
						const bCountryCallingCode = b.partner.phone?.substring(0, 4).replace("+", "");
						let aCountry;
						let bCountry;

						if (aCountryCallingCode) {
							aCountry = countries
								.find((elem: any) => elem.callingCodes.includes(aCountryCallingCode))
								?.alpha2Code?.toLowerCase();
						}

						if (bCountryCallingCode) {
							bCountry = countries
								.find((elem: any) => elem.callingCodes.includes(bCountryCallingCode))
								?.alpha2Code?.toLowerCase();
						}
						return aCountry.localeCompare(bCountry);
					},
					render: (partner: any) => {
						if (!partner) return "-";
						const countryCallingCode = partner.phone?.substring(0, 4).replace("+", "");
						let country;

						if (countryCallingCode) {
							// console.log("🚀 ~ Binary ~ renderMovements ~ countryCallingCode:", countryCallingCode + "11");

							country = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCode));
							// console.log("🚀 ~ Binary ~ renderMovements ~ country:", country)
						}

						return (
							<span className="TableRowAligned">
								{country ? (
									<div className="react-tel-input">
										<div className="selected-flag">
											<span className={`flag ${country.alpha2Code?.toLowerCase()}`} />
										</div>
									</div>
								) : null}
								{country?.name}
								{/* {Boolean(partner?.userCode) ? ` (#${partner.userCode})` : ""} */}
							</span>
						);
					}
				},
				{
					title: "Nome",
					dataIndex: "partner",
					key: "name",
					render: (partner: any) => {
						return partner?.name || "-";
					},
					sorter: (a: any, b: any) => a.partner.name.localeCompare(b.partner.name),
					...getColumnFilter(["partner.name", "partner.userCode", "partner.email"])
				},
				{
					title: "Nível de Sucesso na data",
					dataIndex: "partner",
					key: "successLevel",
					sorter: (a: any, b: any) => {
						const successLevelOrder = [
							"junior",
							"executive",
							"special-executive",
							"bronze",
							"silver",
							"gold",
							"gold30",
							"gold60",
							"gold100",
							"ruby",
							"emerald",
							"diamond",
							"diamond2",
							"diamond4",
							"diamond6",
							"diamond8",
							"special-diamond"
						];

						const levelA = a.partner?.successLevel;
						const levelB = b.partner?.successLevel;

						// if both levels are defined, sort based on the order in the successLevelOrder array
						if (levelA && levelB) {
							return successLevelOrder.indexOf(levelA) - successLevelOrder.indexOf(levelB);
						}

						// if not, sort based on whether they are defined or not
						return (levelA ? 1 : 0) - (levelB ? 1 : 0);
					},
					render: (partner: any) => {
						return getSuccessLevelBadge(partner?.successLevel) || "-";
					}
				},
				{
					title: "Volume Pessoal Total Acumulado no período",
					dataIndex: "tpv",
					key: "tpv",
					render: (tpv: any) => tpv || 0,
					sorter: (a: any, b: any) => a.tpv - b.tpv
				},
				{
					title: "Designação do Incentivo",
					dataIndex: "notes",
					key: "notes",
					render: (notes: any) => notes || "-",
					sorter: (a: any, b: any) => a.notes?.localeCompare(b.notes)
				}
			);
		}

		const totalMade = data.reduce((acc: number, val: any) => acc + val.value, 0);
		const cyclesMade = data.reduce((acc: number, val: any) => acc + val.binaryPartner.cycles, 0);

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="refresh" />
						<h2>{strings.wallet.movements}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<p>{strings.formatString(strings.wallet.earnedPeriod, formatPrice(totalMade))}</p>

						<button onClick={this.exportData} className="EthicAddButton">
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					scroll={{ x: "max-content" }}
					style={{ marginTop: 20 }}
					columns={columns}
					dataSource={data}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	exportPartnerData = () => {
		const { data: exportData = [], partner } = this.state;
		const { countries = [], partnerOptions = [] } = this.props;

		const columns: any[] = [
			{
				label: "Data",
				value: (row: any) => DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm")
			},
			{
				label: "Valor",
				value: (row: any) => formatPrice(row.value)
			},
			{
				label: "Validade",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "-")
			}
		];

		if (!partner) {
			columns.splice(
				1,
				0,
				// ID
				{
					label: "ID Parceiro",
					value: (row: any) => `#${row.partner?.userCode || "-"}`
				},
				// País
				{
					label: "País",
					value: (row: any) => {
						const phone = row.partner?.phone;
						if (!phone) return "-";

						const callingCode = phone.substring(0, 4).replace("+", "");
						const country = countries.find((c: any) => c.callingCodes.includes(callingCode));

						return country?.name || "-";
					}
				},
				// Nome
				{
					label: "Nome",
					value: (row: any) => row.partner?.name || "-"
				},
				// Nível de Sucesso
				{
					label: "Nível de Sucesso (na data)",
					value: (row: any) => getSuccessLevelBadge(row.partner?.successLevel) || "-"
				},
				// Cycles
				{
					label: "Volume Pessoal Acumulado",
					value: (row: any) => row.tpv || 0
				},
				// Incentivo
				{
					label: "Designação do Incentivo",
					value: (row: any) => row.notes || "-"
				}
			);
		}

		const sheets = [
			{
				sheet: "Movimentos",
				columns,
				content: exportData
			}
		];

		let name = "Movimentos";
		if (partner) {
			const partnerData = partnerOptions.find((elem: any) => elem._id === partner);
			if (partnerData) name = partnerData.name;
		}

		xlsx(sheets, xlsxSettings(name));
	};

	// exportPartnerData = () => {
	// 	const { partners } = this.state;

	// 	const columns = [
	// 		{ label: "Nome", value: "name" },
	// 		{ label: "TPV Acumulado", value: "partner.points.accumulatedTpv" }
	// 	];

	// 	const data = [
	// 		{
	// 			sheet: `Parceiros`,
	// 			columns,
	// 			content: partners
	// 		}
	// 	];

	// 	xlsx(data, xlsxSettings(`Parceiros`));
	// };

	exportPartnerDetailData = () => {
		const { movements = [], partnerDetail } = this.state;

		const columns = [
			{ label: "Data", value: (row: any) => DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm") },
			{ label: "Descrição", value: "notes" },
			{ label: "Bónus", value: (row: any) => (row.value != null ? formatPrice(row.value) : capitalize(row.prize)) },
			{ label: "TPV Acumulado", value: "tpv" },
			{
				label: "Data de Expiração",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "-")
			}
		];

		const data = [
			{
				sheet: `Incentivos Extra - ${partnerDetail.name}`,
				columns,
				content: movements
			}
		];

		xlsx(data, xlsxSettings(`Incentivos Extra - ${partnerDetail.name}`));
	};

	renderPartnerMovements() {
		const { movements = [] } = this.state;

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.commissions.incentives}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<button className="EthicAddButton" onClick={this.exportPartnerDetailData}>
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					columns={[
						{
							title: strings.fields.date,
							dataIndex: "date",
							key: "date",
							render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
							width: 150
						},
						{
							title: strings.fields.description,
							dataIndex: "notes",
							key: "notes",
							render: (notes: string) => notes || strings.bonuses.bonusExtraIncentivePrize, // If it does not have notes it is because it comes from the reports collection
							sorter: (a, b) => a.description?.localeCompare(b.description)
						},
						{
							title: strings.generic.bonus,
							dataIndex: "bonus",
							key: "bonus",
							render: (_: any, record: any) => {
								console.log("🚀 ~ Incentives ~ renderPartnerMovements ~ record:", record);
								if (record.value != null) return formatPrice(record.value);
								return capitalize(record.prize);
							}
						},
						{
							title: strings.commissions.accumulatedTpv,
							dataIndex: "tpv",
							key: "tpv",
							render: (tpv: number) => tpv || 0
						},
						{
							title: strings.generic.expirationDate,
							dataIndex: "expirationDate",
							key: "expirationDate",
							render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
							align: "center",
							sorter: (a, b) => DateTime.fromISO(a.expirationDate).toMillis() - DateTime.fromISO(b.expirationDate).toMillis(),
							width: 150
						}
					]}
					dataSource={movements}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	get filtration() {
		const { globalSearch, columnSearch } = this.state;
		return {
			showGlobalSearch: true,
			showColumnSearch: true,
			defaultValues: { globalSearch, columnSearch },
			onGlobalSearch: (globalSearch: string) => {
				this.setState({ globalSearch, page: 0 }, () => this.getData());
			},
			onColumnSearch: (columnSearch: string) => {
				this.setState({ columnSearch, page: 0 }, () => this.getData());
			}
		};
	}

	renderPartners() {
		const { partners, partner } = this.state;
		const { countries } = this.props;

		if (partner) return null;

		return (
			<Col xs={24}>
				<ContentWrapper>
					<div className="ScreenHeader --multi">
						<div className="ScreenHeaderLeft">
							<Icon name="diagram" />
							<h2>{strings.sidebar.partners}</h2>
						</div>
						<div className="ScreenHeaderRight">
							<button className="EthicAddButton" onClick={this.exportPartnerData}>
								<Icon name="print" />
							</button>
						</div>
					</div>
					<Table
						style={{ marginTop: 20 }}
						columns={[
							{
								title: strings.fields.partner,
								dataIndex: "name",
								key: "name",
								render: (name: string, record: any) => {
									if (!name) return "-";
									const countryCallingCode = record.phone?.substring(0, 4).replace("+", "");
									let country;

									if (countryCallingCode) {
										country = countries
											.find((elem: any) => elem.callingCodes.includes(countryCallingCode))
											?.alpha2Code?.toLowerCase();
									}

									return (
										<span className="TableRowAligned">
											{country ? (
												<div className="react-tel-input">
													<div className="selected-flag">
														<span className={`flag ${country}`} />
													</div>
												</div>
											) : null}
											{record.name}
											{Boolean(record?.userCode) ? ` (#${record.userCode})` : ""}
										</span>
									);
								},
								sorter: (a, b) => a.name?.localeCompare(b.name),
								...getColumnFilter("name")
							},
							{
								title: strings.users.personalVolumeAccumulated,
								dataIndex: "points",
								key: "points",
								render: (_: any, record: any) => record?.partner?.points?.accumulatedTpv || 0,
								sorter: (a, b) => a.partner?.points?.accumulatedTpv - b.partner?.points?.accumulatedTpv,
								width: 250
							}
						]}
						dataSource={partners}
						rowKey={(record) => record._id}
					/>
				</ContentWrapper>
			</Col>
		);
	}

	renderProgressBar() {
		const { data, accumulatedTpv, partnerDetail } = this.state;

		const steps = [];
		for (const incentive of data) {
			steps.push({
				value: incentive.tpv,
				element: incentive.type === "money" ? <small>{formatPrice(incentive.bonus)}</small> : <small>{incentive.prize}</small>,
				topElement: <small>{incentive.tpv}</small>
			});
		}

		steps.sort((a, b) => a.value - b.value);

		if (!steps.length) return null;

		return <ProgressBarSteps min={0} steps={steps} value={accumulatedTpv + partnerDetail?.partner?.points?.tpv || 0} />;
	}

	renderIncentive() {
		const { accumulatedTpv, partnerDetail, settings } = this.state;

		let currentBonus = {
			isActive: true,
			appliedRewards: true,
			_id: "65a011d164ab8069e2d73d3c",
			startDate: "2025-11-01T00:00:00.000Z",
			endDate: "2026-01-01T00:00:00.000Z",
			incentives: [
				{
					type: "money",
					_id: "6557a0e7db4cbf01d335a962",
					tpv: 1200,
					bonus: 25,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a04e9151cb0e4dd1ad3f",
					tpv: 1500,
					bonus: 35,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a04e9151cb0e4dd1ad40",
					tpv: 2250,
					bonus: 60,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a04e9151cb0e4dd1ad41",
					tpv: 3750,
					bonus: 110,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a04e9151cb0e4dd1ad42",
					tpv: 6000,
					bonus: 200,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a0b09151cb0e4dd1ad7f",
					tpv: 9000,
					bonus: 350,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a0b09151cb0e4dd1ad80",
					tpv: 12000,
					bonus: 550,
					prize: ""
				},
				{
					type: "money",
					_id: "6582a0b09151cb0e4dd1ad81",
					tpv: 15000,
					bonus: 700,
					prize: ""
				}
			],
			name: {
				pt: "Teste 2025",
				en: "",
				fr: ""
			}
		};

		let options: Array<{ value: string; label: string }> =
			settings?.bonuses?.activeTrimestral
				?.filter((bonus: any) => bonus.isActive)
				?.map((bonus: any) => ({
					value: bonus._id,
					label: translate(bonus.name)
				})) || [];

		if (!currentBonus) return null;

		return (
			<ContentWrapper>
				<div className="ScreenHeader" style={{ justifyContent: "space-between" }}>
					<div className="ScreenHeaderLeft">
						<Icon name="points" />
						<h2>{strings.commissions.progressAdditional}</h2>
					</div>
					<Select
						defaultValue={options.find((option) => option.value === currentBonus._id)?.label || ""}
						style={{ width: 120, paddingBottom: 10 }}
						onChange={(value: string) => {
							this.getData(value);
						}}
						options={options}
					/>
				</div>
				<Row gutter={[20, 20]}>
					<Col xs={24}>{this.renderProgressBar()}</Col>
					<Col xs={24} md={6}>
						<div className="IconCardOuterContainer">
							<div className="IconCard">
								<div className="IconCardHeader">
									<div className="IconCardHeaderMask" />
									<div className="IconCardType">
										<Icon name="points" />
									</div>
								</div>
								<div className="IconCardBody">
									<span className="IconCardValue">{accumulatedTpv + partnerDetail?.partner?.points?.tpv || 0}</span>
									<h3 className="IconCardDescription">{strings.commissions.accumulatedTpv}</h3>
								</div>
							</div>
						</div>
					</Col>
					<Col xs={24} md={8}>
						<table className="CommissionTpvTable">
							<thead>
								<tr>
									<th>{strings.commissions.accumulatedTpv}</th>
									<th>{strings.generic.bonus}</th>
								</tr>
							</thead>
							<tbody>
								{currentBonus?.incentives?.map((entry: any, index: number) => {
									const reached = accumulatedTpv + partnerDetail?.partner?.points?.tpv >= entry.tpv;

									const style: React.CSSProperties = {};
									if (reached) {
										style.backgroundColor = "#00b5b033";
										style.fontWeight = 600;
										style.color = "#00b5b0";
									}

									return (
										<tr key={index} style={style}>
											<td>{entry.tpv}</td>
											<td>{entry.type === "money" ? formatPrice(entry.bonus) : entry.prize}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</Col>
				</Row>
			</ContentWrapper>
		);
	}

	renderProgressBarTrip(bonus: any) {
		const { partnerDetail } = this.state;

		const steps = [];
		for (const incentive of bonus.bonuses) {
			steps.push({
				value: incentive.annual,
				element: <small>{incentive.prize}</small>,
				topElement: <small>{incentive.annual}</small>
			});
		}

		steps.sort((a, b) => a.value - b.value);

		if (!steps.length) return null;

		return <ProgressBarSteps min={0} steps={steps} value={partnerDetail?.partner?.points?.annualTpv || 0} />;
	}

	renderTrip() {
		const { settings, partnerDetail } = this.state;
		const { bonuses } = settings || {};
		const groupTrip = bonuses?.groupTrip;

		if (!groupTrip) return null;

		return (
			<ContentWrapper>
				<div className="ScreenHeader">
					<div className="ScreenHeaderLeft">
						<Icon name="points" />
						<h2>{strings.commissions.progressTrip}</h2>
					</div>
				</div>
				<Row gutter={[20, 20]}>
					<Col xs={24}>{this.renderProgressBarTrip(groupTrip)}</Col>
					<Col xs={24} md={6}>
						<div className="IconCardOuterContainer">
							<div className="IconCard">
								<div className="IconCardHeader">
									<div className="IconCardHeaderMask" />
									<div className="IconCardType">
										<Icon name="points" />
									</div>
								</div>
								<div className="IconCardBody">
									<span className="IconCardValue">{partnerDetail?.partner?.points?.annualTpv || 0}</span>
									<h3 className="IconCardDescription">{strings.commissions.accumulatedTpv}</h3>
								</div>
							</div>
						</div>
					</Col>
					<Col xs={24} md={8}>
						<table className="CommissionTpvTable">
							<thead>
								<tr>
									<th>{strings.commissions.accumulatedTpv}</th>
									<th>{strings.generic.bonus}</th>
								</tr>
							</thead>
							<tbody>
								{groupTrip?.bonuses?.map((entry: any, index: number) => {
									const reached = partnerDetail?.partner?.points?.annualTpv >= entry.annual;

									const style: React.CSSProperties = {};
									if (reached) {
										style.backgroundColor = "#00b5b033";
										style.fontWeight = 600;
										style.color = "#00b5b0";
									}

									return (
										<tr key={index} style={style}>
											<td>{entry.annual}</td>
											<td>{entry.prize}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</Col>
				</Row>
			</ContentWrapper>
		);
	}

	renderTrimestralExpanded(row: any) {
		const { activeTrimestral } = this.state;

		return (
			<div className="IncentivesSubTable">
				<table>
					<thead>
						<tr>
							<th>{strings.settings.type}</th>
							<th>{strings.commissions.tpv}</th>
							<th>{strings.bonuses.bonusExtraIncentivePrize}</th>
						</tr>
					</thead>
					<tbody>
						{row.original.incentives?.map((incentive: any, index: number) => {
							return (
								<tr key={index}>
									<td>
										<div className="IncentiveSubTableSelect">
											<Select
												value={incentive.type}
												onChange={(elem: any) => {
													incentive.type = elem;
													incentive.tpv = 0;
													incentive.prize = "";
													incentive.bonus = 0;

													this.setState({ incentive, hasUnsavedFields: true });
												}}
												disabled={true}
												placeholder={strings.settings.type}
												style={{ height: 32, width: "100%", maxWidth: 200 }}
											>
												<Select.Option value="money">{strings.bonuses.bonusExtraIncentiveMoney}</Select.Option>
												<Select.Option value="prize">{strings.bonuses.bonusExtraIncentivePrize}</Select.Option>
											</Select>
										</div>
									</td>
									<td>
										<div className="IncentivesSubTableInput">
											<Input
												type="number"
												value={incentive.tpv}
												placeholder="0"
												addonAfter="pts"
												min={0}
												disabled={true}
												style={{ maxWidth: 200 }}
												className="bonus-input-range"
												onChange={(val: any) => {
													if (val.target.value === "") {
														incentive.tpv = null;
														this.setState({
															activeTrimestral,
															hasUnsavedFields: true
														});

														return;
													}

													if (isNaN(val.target.value)) return;

													const number = Number(val.target.value);

													incentive.tpv = Number(number);
													this.setState({ activeTrimestral, hasUnsavedFields: true });
												}}
												onBlur={() => {
													this.setState({ activeTrimestral, hasUnsavedFields: true });
												}}
											/>
										</div>
									</td>
									<td>
										<div className="IncentivesSubTableInput">
											{incentive.type === "money" && (
												<Input
													type="number"
													value={incentive.bonus}
													placeholder="0"
													addonAfter="€"
													min={0}
													disabled={true}
													style={{ maxWidth: 200 }}
													className="bonus-input-range"
													onChange={(val: any) => {
														if (val.target.value === "") {
															incentive.bonus = null;
															this.setState({
																activeTrimestral,
																hasUnsavedFields: true
															});

															return;
														}

														if (isNaN(val.target.value)) return;

														const number = Number(val.target.value);

														incentive.bonus = Number(number);
														this.setState({
															activeTrimestral,
															hasUnsavedFields: true
														});
													}}
													onBlur={() => {
														this.setState({
															activeTrimestral,
															hasUnsavedFields: true
														});
													}}
												/>
											)}
											{incentive.type === "prize" && (
												<Input
													disabled={true}
													value={incentive.prize}
													style={{ width: "100%", height: 32 }}
													placeholder={strings.bonuses.bonusExtraIncentivePrize}
													onChange={(e: any) => {
														incentive.prize = e.target.value;
														this.setState({ activeTrimestral, hasUnsavedFields: true });
													}}
												/>
											)}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				{(!row.original.incentives || row.original.incentives?.length === 0) && (
					<div className="IncentivesSubTableEmpty">
						<p>{strings.settings.noIncentives}</p>
					</div>
				)}
			</div>
		);
	}

	renderTrimestral() {
		const { settings } = this.state;

		return (
			<CompTable
				title={{ title: strings.settings.incentives }}
				data={settings?.bonuses?.activeTrimestral}
				columns={[
					{
						Header: strings.dashboard.period,
						id: "period",
						accessor: (row: any) =>
							DateTime.fromISO(row.startDate).toUTC().toFormat("dd/MM/yyyy") +
							" - " +
							DateTime.fromISO(row.endDate).toUTC().toFormat("dd/MM/yyyy"),
						maxWidth: 200
					},
					{
						Header: strings.fields.name,
						id: "name",
						accessor: (row: any) => translate(row.name) || "-"
					}
				]}
				renderExpanded={(row: any) => this.renderTrimestralExpanded(row)}
				expanded
				paginated
			/>
		);
	}

	renderGroupTripIntervals = () => {
		const { groupTrip } = this.state;
		const { bonuses: groupTripBonuses } = groupTrip;

		return groupTripBonuses?.map((interval: any, index: any) => {
			return (
				<React.Fragment key={index}>
					<Col xs={24} md={14}>
						<Row gutter={5} justify="start">
							<Col xs={11}>
								<Input
									type="number"
									value={interval.average}
									placeholder="0"
									disabled={true}
									addonAfter="pts"
									min={index === 0 ? 0 : groupTripBonuses[index - 1].average + 1}
									className="bonus-input-range"
									onChange={(val: any) => {
										if (isNaN(val.target.value)) return;

										const number = Number(val.target.value);

										interval.average = Number(number);
										this.setState({ groupTrip, hasUnsavedFields: true });
									}}
									onBlur={() => {
										if (groupTripBonuses[index - 1] && interval.average < groupTripBonuses[index - 1].average) {
											interval.average = groupTripBonuses[index - 1].average + 1;
											this.setState({ groupTrip, hasUnsavedFields: true });
										}
									}}
								/>
							</Col>
							<Col xs={2}>
								<div className="bonus-input-separator">
									<span>{strings.successLadder.or}</span>
								</div>
							</Col>
							<Col xs={11}>
								<Input
									type="number"
									value={interval.annual}
									placeholder="0"
									addonAfter="pts"
									min={index === 0 ? 0 : groupTripBonuses[index - 1].annual + 1}
									className="bonus-input-range"
									disabled={true}
									onChange={(val: any) => {
										if (isNaN(val.target.value)) return;

										const number = Number(val.target.value);

										interval.annual = Number(number);
										this.setState({ groupTrip, hasUnsavedFields: true });
									}}
									onBlur={() => {
										if (groupTripBonuses[index - 1] && interval.annual < groupTripBonuses[index - 1].annual) {
											interval.annual = groupTripBonuses[index - 1].annual + 1;
											this.setState({ groupTrip, hasUnsavedFields: true });
										}
									}}
								/>
							</Col>
						</Row>
					</Col>
					<Col xs={24} md={10}>
						<div className="bonus-range-wrapper">
							<Input
								value={interval.prize}
								placeholder={strings.bonuses.bonusTripGroupHeader3}
								disabled={true}
								style={{ height: 32 }}
								onChange={(e) => {
									interval.prize = e.target.value;

									this.setState({ groupTrip, hasUnsavedFields: true });
								}}
							/>
						</div>
					</Col>
				</React.Fragment>
			);
		});
	};

	renderGroupTrip() {
		const { settings } = this.state;

		const groupTrip = settings?.bonuses?.groupTrip;
		console.log("🚀 ~ Incentives ~ renderGroupTrip ~ groupTrip:", groupTrip);
		if (!groupTrip) return null;

		const { bonuses: groupTripBonuses } = groupTrip;
		console.log("🚀 ~ Incentives ~ renderGroupTrip ~ groupTripBonuses:", groupTripBonuses);
		const isActive = groupTrip.isActive;

		let buttonDisabled = false;
		let last: any = {};

		if (groupTripBonuses && groupTripBonuses.length > 0) {
			last = groupTripBonuses[groupTripBonuses.length - 1];
			const lastFilled =
				(last?.average !== void 0 &&
					last?.average >= 0 &&
					last?.annual !== void 0 &&
					last?.annual >= 0 &&
					last?.prize !== "" &&
					last?.prize?.trim().length > 0) ||
				false;

			buttonDisabled = !lastFilled;
		} else {
			buttonDisabled = false;
		}

		return (
			<ContentWrapper extraClass="GroupTripContent">
				<div className="bonus-title-wrapper">
					<div className="bonus-title" style={{ marginBottom: 0 }}>
						{strings.bonuses.bonusTripGroupTitle}
					</div>
				</div>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={14}>
						<Row>
							<Col xs={11}>
								<b>{strings.bonuses.bonusTripGroupHeader1}</b>
							</Col>
							<Col xs={{ span: 11, offset: 2 }}>
								<b>{strings.bonuses.bonusTripGroupHeader2}</b>
							</Col>
						</Row>
					</Col>
					<Col xs={24} md={10}>
						<label>
							<b>{strings.bonuses.bonusTripGroupHeader3}</b>
						</label>
					</Col>
					{this.renderGroupTripIntervals()}
				</Row>
			</ContentWrapper>
		);
	}

	render() {
		const { eligible, partner, partnerDetail } = this.state;

		return (
			<div className="CommissionIncentives">
				<Helmet>
					<title>{strings.commissions.incentives}</title>
					<meta name="description" content="Description of incentives" />
				</Helmet>
				<div className="IconCards">
					<Row gutter={[20, 20]}>
						{Boolean(partner) && !eligible && (
							<Col xs={24}>
								<ContentWrapper>
									<div className="ScreenHeader">
										<div className="ScreenHeaderLeft">
											<Icon name="user" />
											<h2>{strings.commissions.eligibility}</h2>
										</div>
									</div>
									<p>
										<strong>
											{strings.commissions.actualRank}: {getSuccessLevelBadge(partnerDetail?.partner?.successLevel)}
										</strong>
									</p>
									<p className="CommissionsPartnersAttention">{strings.commissions.notEligible}</p>
								</ContentWrapper>
							</Col>
						)}
						{Boolean(partner) && (
							<React.Fragment>
								<Col xs={24}>{this.renderIncentive()}</Col>
								<Col xs={24}>{this.renderTrip()}</Col>
							</React.Fragment>
						)}
						{this.renderCards()}
						{this.renderPartners()}

						<Col xs={24}>{this.renderTrimestral()}</Col>
						<Col xs={24}>{this.renderGroupTrip()}</Col>
						<Col xs={24}>{this.renderMovements()}</Col>
					</Row>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(Incentives);
